import firebase from "firebase/app";

import { Icon } from "semantic-ui-react";

import React from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import getDistributionChannelsQuery from "astrid-firestore/src/api/distribution/getDistributionChannelsQuery";
import { useCollectionData } from "astrid-firestore/src/hooks";

import Flex from "../../../../components/Flex/Flex";
import Button from "../../../ui/components/Buttons/Button";
import DropdownMenu from "../../../ui/components/DropdownMenu/DropdownMenu";
import InstantSearch from "../../../ui/components/InstantSearch/InstantSearch";
import InstantSearchBox from "../../../ui/components/InstantSearch/InstantSearchBox";
import InstantSearchClear from "../../../ui/components/InstantSearch/InstantSearchClear";
import InstantSearchHits from "../../../ui/components/InstantSearch/InstantSearchHits";
import InstantSearchHitsPerPage from "../../../ui/components/InstantSearch/InstantSearchHitsPerPage";
import InstantSearchIndexUiState from "../../../ui/components/InstantSearch/InstantSearchIndexUiState";
import InstantSearchNumberOfHits from "../../../ui/components/InstantSearch/InstantSearchNumberOfHits";
import InstantSearchPagination from "../../../ui/components/InstantSearch/InstantSearchPagination";
import InstantSearchPresets from "../../../ui/components/InstantSearch/InstantSearchPresets";
import InstantSearchRefinements from "../../../ui/components/InstantSearch/InstantSearchRefinements";
import InstantSearchRefresh from "../../../ui/components/InstantSearch/InstantSearchRefresh";
import PageView from "../../../ui/views/PageView/PageView";

import algoliaFiltersFromUiState from "../../../algolia/utils/algoliaFiltersFromUiState";

import articlesExportCSV from "../../api/articlesExportCSV";
import useArticlesRefinements from "../../hooks/useArticlesRefinements";

export default function ArticlesView({ organization }) {
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const navigate = useNavigate();

	const [channels] = useCollectionData(getDistributionChannelsQuery(firebase));

	const refinements = useArticlesRefinements({ organization, channels });

	const presetsPath = `articles-${organization?.id || "admin"}`;

	const configure = {
		filters: organization && `${organization.type}.id:${organization.id}`,
	};

	return (
		<InstantSearch routing indexName="articles" configure={configure}>
			<InstantSearchRefresh refreshInterval={5000} />

			<PageView
				header={t("articles", "Articles")}
				sidebar={
					<>
						<InstantSearchBox />

						<InstantSearchRefinements refinements={refinements} />
					</>
				}
				maxWidth={1600}
			>
				<Flex gap={10} justifyContent="flex-start">
					<Flex gap={10} justifyContent="flex-start">
						<InstantSearchPresets path={presetsPath} refinements={refinements} />

						<InstantSearchClear />

						<InstantSearchNumberOfHits />
					</Flex>

					<InstantSearchHitsPerPage />

					<Button
						icon
						basic
						size="small"
						circular
						active={!pathname.includes("calendar")}
						onClick={() => navigate(`../articles${window.location.search}`)}
					>
						<Icon name="bars" />
					</Button>

					<Button
						icon
						basic
						size="small"
						circular
						active={pathname.includes("calendar")}
						onClick={() => navigate(`calendar${window.location.search}`)}
					>
						<Icon name="calendar alternate " />
					</Button>

					<InstantSearchIndexUiState>
						{({ indexUiState }) => {
							return (
								<DropdownMenu
									options={[
										{
											text: t("exportCSV", "Export CSV"),
											action: () => {
												const { query, filters } = algoliaFiltersFromUiState(indexUiState);

												return articlesExportCSV({ query, filters });
											},
										},
									]}
								/>
							);
						}}
					</InstantSearchIndexUiState>
				</Flex>

				<InstantSearchHits>
					{({ data }) => {
						return <Outlet context={{ data, organization, channels }} />;
					}}
				</InstantSearchHits>

				<InstantSearchPagination />
			</PageView>
		</InstantSearch>
	);
}
